import { ProviderProfilePage } from "@/features/profile/components/ProviderProfilePage";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LockKeyhole } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTRPC } from "@/trpc";
import Loading from "@/components/Loading";
import { useProviderProfileData } from "@/features/profile/hooks/useProviderProfileData";

export const Route = createFileRoute("/_admin/admin/provider-profile/$userId/")(
  {
    errorComponent: ({ error }) => {
      console.error(error);
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-8">
          <Card className="max-w-md">
            <CardHeader>
              <div className="text-center">
                <LockKeyhole className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-2xl font-semibold">An Error Occurred</h2>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center">
                We encountered an error while trying to load this profile. This
                may be because you don't have permission to view it. If you
                believe you should have access, please try again later.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    },
    component: AdminProviderProfileRoute,
  },
);

function AdminProviderProfileRoute() {
  const { userId } = Route.useParams();
  const trpc = useTRPC();
  const providerUserQuery = useQuery(
    trpc.profile.getSharedUser.queryOptions({
      userId,
    }),
  );
  const profileData = useProviderProfileData(userId);

  if (providerUserQuery.error) {
    throw providerUserQuery.error;
  }

  if (profileData.error) {
    throw profileData.error;
  }

  if (
    providerUserQuery.isLoading ||
    !providerUserQuery.data ||
    profileData.isLoading ||
    !profileData.data
  ) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loading className="h-16 w-16" />
      </div>
    );
  }

  return (
    <ProviderProfilePage
      user={providerUserQuery.data}
      credentials={profileData.data.credentials}
      jobPreferences={profileData.data.jobPreferences}
      sharePermissions={profileData.data.sharePermissions}
      allowDocumentUploads={true}
    />
  );
}
